import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';

type FavoriteAssignmentParams = {
  assignmentId: number;
};

type FavoriteData = {
  professionalAssignmentFavorites: Array<{
    assignment_id: number;
    id?: number;
    assignment?: unknown;
  }>;
};

type MutationContext = {
  previousFavorites: Array<[readonly unknown[], unknown]>;
};

const TEMP_ID_GENERATOR = () => Date.now();

const OPERATION_NAMES = {
  PROFESSIONAL: 'GetFavoritedAssignmentsForFavoriteButton',
  AGENCY_IRP: 'GetFavoritedAssignmentsIRPForFavoriteButton',
} as const;

export const useFavoriteAssignmentsMutations = () => {
  const queryClient = useQueryClient();
  const { axios } = useNetworkRequest();

  const markAsFavorite = (id: number) => axios.post(`/v3/professional/assignments/${id}/favorite`);
  const unmarkAsFavorite = (id: number) =>
    axios.delete(`/v3/professional/assignments/${id}/favorite`);

  const isFavoriteQuery = (queryKey: readonly unknown[]) =>
    queryKey[0] === OPERATION_NAMES.PROFESSIONAL || queryKey[0] === OPERATION_NAMES.AGENCY_IRP;

  const syncFavoriteQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query) => isFavoriteQuery(query.queryKey),
    });
  };

  const ensureData = (old?: FavoriteData): FavoriteData =>
    old ?? { professionalAssignmentFavorites: [] };

  const createOptimisticUpdate =
    (isAdding: boolean) =>
    async ({ assignmentId }: FavoriteAssignmentParams) => {
      await queryClient.cancelQueries({
        predicate: (query) => isFavoriteQuery(query.queryKey),
      });

      const previousFavorites = queryClient.getQueriesData({
        predicate: (query) => isFavoriteQuery(query.queryKey),
      });

      const findAssignmentInCache = () => {
        const favoriteQueries = queryClient.getQueriesData({
          predicate: (query) => isFavoriteQuery(query.queryKey),
        });

        for (const [, queryData] of favoriteQueries) {
          if (queryData && typeof queryData === 'object') {
            const data = queryData as Record<string, unknown>;
            if (
              data.professionalAssignmentFavorites &&
              Array.isArray(data.professionalAssignmentFavorites)
            ) {
              const favorite = data.professionalAssignmentFavorites.find(
                (fav: { assignment_id: number; assignment?: unknown }) =>
                  fav.assignment_id === assignmentId,
              );
              if (favorite?.assignment) {
                return favorite.assignment;
              }
            }
          }
        }
        return null;
      };

      queryClient.setQueriesData(
        { predicate: (query) => isFavoriteQuery(query.queryKey) },
        (old: unknown) => {
          const data = ensureData(old as FavoriteData);

          if (isAdding) {
            if (
              data.professionalAssignmentFavorites.some((fav) => fav.assignment_id === assignmentId)
            ) {
              return { ...data };
            }

            const assignment = findAssignmentInCache();
            if (assignment) {
              return {
                ...data,
                professionalAssignmentFavorites: [
                  ...data.professionalAssignmentFavorites,
                  {
                    assignment_id: assignmentId,
                    id: TEMP_ID_GENERATOR(),
                    assignment,
                  },
                ],
              };
            }

            return { ...data };
          }

          return {
            ...data,
            professionalAssignmentFavorites: data.professionalAssignmentFavorites.filter(
              (fav) => fav.assignment_id !== assignmentId,
            ),
          };
        },
      );

      return { previousFavorites };
    };

  const handleMutationError =
    (operation: string) => (err: Error, _: FavoriteAssignmentParams, context?: MutationContext) => {
      if (context?.previousFavorites) {
        context.previousFavorites.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error(`Error ${operation} assignment as favorite:`, err);
    };

  const createMutation = (
    mutationFn: (params: FavoriteAssignmentParams) => Promise<unknown>,
    isAdding: boolean,
    operation: string,
  ) =>
    useMutation({
      mutationFn,
      onMutate: createOptimisticUpdate(isAdding),
      onError: handleMutationError(operation),
      onSettled: syncFavoriteQueries,
    });

  const markAsFavoriteMutation = createMutation(
    ({ assignmentId }) => markAsFavorite(assignmentId),
    true,
    'marking',
  );

  const unmarkAsFavoriteMutation = createMutation(
    ({ assignmentId }) => unmarkAsFavorite(assignmentId),
    false,
    'unmarking',
  );

  return {
    markAsFavoriteMutation: markAsFavoriteMutation.mutate,
    unmarkAsFavoriteMutation: unmarkAsFavoriteMutation.mutate,
    isMarkingFavorite: markAsFavoriteMutation.isLoading,
    isUnmarkingFavorite: unmarkAsFavoriteMutation.isLoading,
    isLoading: markAsFavoriteMutation.isLoading || unmarkAsFavoriteMutation.isLoading,
  };
};
